import { aiPost } from '@/components/ai-employee/hooks/use-ai-api';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';

/**
 * Keeps Google Calendar up to date with Chekkit jobs.
 *
 * A location can choose to have its scheduled jobs added to the Google
 * Calendar it connected for AI Employee booking. The server works out what
 * needs to change; this only asks it to check, after a job changes here or
 * when the calendar opens. It does nothing for locations that have not
 * turned that on.
 */

export interface JobSyncResult {
  status:
    | 'synced'
    | 'incomplete'
    | 'off'
    | 'disconnected'
    | 'reconnect'
    | 'unconfigured'
    | 'calendar_unavailable';
  created?: number;
  updated?: number;
  removed?: number;
  failed?: number;
  remaining?: number;
}

type Auth = { _id?: string; products?: string[] } | null | undefined;

// Several quick changes, such as dragging a few jobs, become one check
const DELAY_MS = 1500;
// The server writes a bounded batch per request; a first sync can take a few
const MAX_RUNS = 8;
// How long to stop asking once the server says the setting is off
const OFF_FOR_MS = 5 * 60 * 1000;

const offUntil = new Map<string, number>();
let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;
let queued: string | null = null;

/** Runs the sync until Google matches the jobs, and returns the last result. */
export async function runJobSync(businessId: string): Promise<JobSyncResult> {
  let result: JobSyncResult = { status: 'off' };

  for (let run = 0; run < MAX_RUNS; run++) {
    result = await aiPost<JobSyncResult>(
      `/api/ai-employee/calendar/${businessId}/job-sync/run`,
      {},
    );

    const wrote = (result.created || 0) + (result.updated || 0) + (result.removed || 0);
    // Stop once nothing is left, or when a run made no progress
    if (result.status !== 'incomplete' || !result.remaining || !wrote) break;
  }

  return result;
}

async function runInBackground(businessId: string) {
  if (running) {
    // A change made while a check is in flight is picked up right after it
    queued = businessId;
    return;
  }

  running = true;
  try {
    const result = await runJobSync(businessId);
    if (result.status === 'off') {
      offUntil.set(businessId, Date.now() + OFF_FOR_MS);
    }
  } catch (error) {
    // The job itself saved; Google catches up on the next change or visit
    console.error('Failed to update Google Calendar with Chekkit jobs:', error);
  } finally {
    running = false;
    if (queued) {
      const next = queued;
      queued = null;
      void runInBackground(next);
    }
  }
}

/** Call after a job or its customer changes, or when the job calendar opens. */
export function pushJobsToGoogleCalendar(auth: Auth) {
  const businessId = auth?._id;
  if (!businessId || !hasAiEmployeeProduct(auth || null)) return;
  if ((offUntil.get(businessId) || 0) > Date.now()) return;

  clearTimeout(timer);
  timer = setTimeout(() => void runInBackground(businessId), DELAY_MS);
}

/** The setting was just changed, so the next job change should check again. */
export function jobSyncSettingChanged(businessId: string) {
  offUntil.delete(businessId);
}
